/* ... existing imports ... */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import PersonalInfo from './PersonalInfo';
import CompetencySection from './CompetencySection';
import SummarySection from './SummarySection';
import { assessmentData } from '../data/assessmentData';
import { generateAssessmentPDF, sendAssessmentEmail } from '../utils/exportUtils';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClipboard, FiDownload, FiCheckCircle, FiLoader, FiMail, FiAlertCircle } = FiIcons;

const LOGO_URL = "https://file-service.questprotocol.xyz/file/66708b76-63d2-4318-b223-99757962c06f";

const AssessmentForm = () => {
  const [personalInfo, setPersonalInfo] = useState({
    clinicianName: '',
    clinicianEmail: '',
    teamLeadName: '',
    teamLeadEmail: '',
    assessmentDate: new Date().toISOString().split('T')[0]
  });

  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSection]);

  const handleRatingChange = (sectionId, itemId, rating) => {
    setRatings(prev => ({ ...prev, [`${sectionId}-${itemId}`]: rating }));
  };

  const handleCommentChange = (sectionId, itemId, comment) => {
    setComments(prev => ({ ...prev, [`${sectionId}-${itemId}`]: comment }));
  };

  const handleExport = async () => {
    // Validation
    if (!personalInfo.clinicianName || !personalInfo.clinicianEmail || !personalInfo.teamLeadEmail) {
      alert("Please complete the Personal Information section first.");
      setCurrentSection(0);
      return;
    }

    setIsExporting(true);
    try {
      const assessmentResults = { 
        personalInfo, 
        ratings: { ...ratings }, 
        comments: { ...comments } 
      };
      
      // Generate PDF
      const doc = await generateAssessmentPDF(assessmentResults, assessmentData);
      
      // File Name
      const timestamp = new Date().toISOString().split('T')[0];
      const safeName = personalInfo.clinicianName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `Clinical_Assessment_${safeName}_${timestamp}.pdf`;
      
      // Trigger Download
      doc.save(fileName);

      // Simulate Email
      await sendAssessmentEmail(assessmentResults);

      // Celebration
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#2e1065', '#581c87', '#7e22ce']
      });
      
      setExportComplete(true);
    } catch (error) {
      console.error("Export component error:", error);
      alert(`Export Failed: ${error.message}. Please check your connection and try again.`);
    } finally {
      setIsExporting(false);
    }
  };

  const sections = [
    'Personal Information',
    ...assessmentData.map(section => section.title),
    'Executive Summary'
  ];

  const nextSection = () => currentSection < sections.length - 1 && setCurrentSection(currentSection + 1);
  const prevSection = () => currentSection > 0 && setCurrentSection(currentSection - 1);
  const isFinalPage = currentSection === sections.length - 1;

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary px-8 py-8 text-white relative">
            <div className="flex justify-between items-start">
              <div className="max-w-[70%]">
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={FiClipboard} className="text-3xl text-purple-300 shrink-0" />
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Clinical Competency</h1>
                </div>
                <p className="text-purple-100 font-medium">InnerWorks Counselling Professional Standard</p>
              </div>
              
              <div className="bg-white p-2 rounded-xl shadow-lg ring-4 ring-purple-500/20 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center overflow-hidden">
                {!logoError ? (
                  <img 
                    src={LOGO_URL} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <SafeIcon icon={FiAlertCircle} className="text-brand-primary text-2xl" />
                )}
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="px-8 py-4 bg-white border-b sticky top-0 z-10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-brand-secondary uppercase tracking-widest">
                Progress: {Math.round(((currentSection + 1) / sections.length) * 100)}%
              </span>
              <span className="text-sm font-semibold text-gray-500">
                Step {currentSection + 1} of {sections.length}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <motion.div
                className="bg-brand-primary h-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              ></motion.div>
            </div>
          </div>

          {/* Body Content */}
          <div className="px-8 py-10 min-h-[450px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSection}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                {currentSection === 0 && (
                  <PersonalInfo personalInfo={personalInfo} setPersonalInfo={setPersonalInfo} />
                )}

                {currentSection > 0 && currentSection <= assessmentData.length && (
                  <CompetencySection
                    section={assessmentData[currentSection - 1]}
                    ratings={ratings}
                    comments={comments}
                    onRatingChange={handleRatingChange}
                    onCommentChange={handleCommentChange}
                  />
                )}

                {isFinalPage && (
                  <SummarySection assessmentData={assessmentData} ratings={ratings} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="px-8 py-6 bg-gray-50 border-t flex justify-between items-center">
            <button
              onClick={prevSection}
              disabled={currentSection === 0 || isExporting}
              className="px-6 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-white hover:border-brand-primary/30 disabled:opacity-30 transition-all"
            >
              Back
            </button>

            <div className="flex gap-4">
              {isFinalPage ? (
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className={`flex items-center gap-2 px-10 py-3 rounded-xl text-white font-bold transition-all shadow-xl shadow-brand-primary/20 ${
                    exportComplete ? 'bg-green-600' : 'bg-brand-primary hover:bg-brand-secondary'
                  } disabled:opacity-70`}
                >
                  {isExporting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="animate-spin" />
                      Generating PDF...
                    </>
                  ) : exportComplete ? (
                    <>
                      <SafeIcon icon={FiCheckCircle} />
                      Done!
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiDownload} />
                      Finalize & Export
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={nextSection}
                  className="px-10 py-3 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-secondary transition-all shadow-lg shadow-brand-primary/10"
                >
                  Next Section
                </button>
              )}
            </div>
          </div>
        </motion.div>
        
        {exportComplete && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-800 shadow-sm"
          >
            <SafeIcon icon={FiMail} className="text-xl shrink-0" />
            <p className="text-sm font-medium">
              Report generated successfully! A copy has been sent to <strong>{personalInfo.clinicianEmail}</strong> and your supervisor.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AssessmentForm;